/**
 * Build an authenticated URL for the protected /api/files/{path} endpoint.
 *
 * Why this exists: the file endpoint is auth-protected, but the browser opens
 * file links in many contexts where cookies and Authorization headers don't
 * travel along — new tabs from mobile in-app webviews (Instagram/WhatsApp
 * Chrome custom tabs), <a download>, <img src> on Safari ITP, etc.
 *
 * Solution: append `?auth=<access_token>` as a fallback the backend already
 * accepts and validates. The token comes from localStorage (same store the
 * Bearer interceptor uses). On the very first session before we ever stored
 * a token (eg right after a cold reload with only HttpOnly cookies), we fall
 * back to the raw URL — the cookies will carry the auth if they exist.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api`;
const TOKEN_KEY = 'habla.access_token';

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function fileUrl(input) {
  if (!input) return '';
  const raw = typeof input === 'string' ? input : (input.url || input.path || '');
  if (!raw) return '';
  const base = raw.startsWith('http') ? raw : `${API}/files/${raw}`;
  const token = getToken();
  if (!token) return base;
  // Don't double-add; respect existing query string.
  const sep = base.includes('?') ? '&' : '?';
  // Only append when the URL points at our own backend — never leak our token
  // to a third party (e.g. if a material's URL was set to an external CDN).
  try {
    const u = new URL(base, window.location.origin);
    const apiHost = new URL(API).host;
    if (u.host !== apiHost) return base;
  } catch { /* fall through to default behaviour */ }
  return `${base}${sep}auth=${encodeURIComponent(token)}`;
}

/**
 * Convert a user avatar value to a displayable URL.
 *
 * Avatars can be:
 *   - An absolute HTTPS URL (Google profile picture, legacy Emergent CDN)
 *   - A relative path like /api/content/habla-academy/avatars/...
 *     stored by the backend — these must be prefixed with the Railway
 *     backend URL because the frontend lives on a different domain.
 */
export function avatarUrl(input) {
  if (!input) return '';
  if (typeof input !== 'string') return '';
  if (input.startsWith('http')) return input;
  const path = input.startsWith('/') ? input : `/${input}`;
  return `${BACKEND}${path}`;
}
